/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnInit,
    AfterViewInit,
    ContentChild,
    forwardRef,
    HostListener
} from '@angular/core';



import DxProgressBar from 'devextreme/ui/progress_bar';

import { DxValidatorComponent } from './validator';

import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';

import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';







const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DxProgressBarComponent),
    multi: true
};

/**
 * The ProgressBar is a widget that shows current progress.
 */

export class DxProgressBarComponent extends DxComponent implements OnDestroy, OnInit, AfterViewInit, ControlValueAccessor {
    instance: DxProgressBar;

    
    validator: DxValidatorComponent;


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies whether the editor's value is valid.
     */
    
    get isValid(): boolean {
        return this._getOption('isValid');
    }
    set isValid(value: boolean) {
        this._setOption('isValid', value);
    }


    /**
     * The maximum value the widget can accept.
     */
    
    get max(): number {
        return this._getOption('max');
    }
    set max(value: number) {
        this._setOption('max', value);
    }


    /**
     * The minimum value the widget can accept.
     */
    
    get min(): number {
        return this._getOption('min');
    }
    set min(value: number) {
        this._setOption('min', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is read-only.
     */
    
    get readOnly(): boolean {
        return this._getOption('readOnly');
    }
    set readOnly(value: boolean) {
        this._setOption('readOnly', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether or not the widget displays a progress status.
     */
    
    get showStatus(): boolean {
        return this._getOption('showStatus');
    }
    set showStatus(value: boolean) {
        this._setOption('showStatus', value);
    }


    /**
     * Specifies a format for the progress status.
     */
    
    get statusFormat(): Function|
        string {
        return this._getOption('statusFormat');
    }
    set statusFormat(value: Function|
        string) {
        this._setOption('statusFormat', value);
    }


    /**
     * Specifies information on the validation error when using a custom validation engine. Should be changed at runtime along with the isValid option.
     */
    
    get validationError(): any {
        return this._getOption('validationError');
    }
    set validationError(value: any) {
        this._setOption('validationError', value);
    }


    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor's value is displayed.
     */
    
    get validationMessageMode(): string {
        return this._getOption('validationMessageMode');
    }
    set validationMessageMode(value: string) {
        this._setOption('validationMessageMode', value);
    }


    /**
     * The current widget value.
     */
    
    get value(): number {
        return this._getOption('value');
    }
    set value(value: number) {
        this._setOption('value', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the complete event.
     */
     onComplete: EventEmitter<any>;

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the valueChanged event.
     */
     onValueChanged: EventEmitter<any>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the isValidChange event.
     */
     isValidChange: EventEmitter<boolean>;

    /**
     * A handler for the maxChange event.
     */
     maxChange: EventEmitter<number>;

    /**
     * A handler for the minChange event.
     */
     minChange: EventEmitter<number>;

    /**
     * A handler for the readOnlyChange event.
     */
     readOnlyChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the showStatusChange event.
     */
     showStatusChange: EventEmitter<boolean>;

    /**
     * A handler for the statusFormatChange event.
     */
     statusFormatChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the validationErrorChange event.
     */
     validationErrorChange: EventEmitter<any>;

    /**
     * A handler for the validationMessageModeChange event.
     */
     validationMessageModeChange: EventEmitter<string>;

    /**
     * A handler for the valueChange event.
     */
     valueChange: EventEmitter<number>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;

     onBlur: EventEmitter<any>;


     change(_) { }
     touched = () => {};



    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            _watcherHelper: WatcherHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'complete', emit: 'onComplete' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'valueChanged', emit: 'onValueChanged' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'isValidChange' },
            { emit: 'maxChange' },
            { emit: 'minChange' },
            { emit: 'readOnlyChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'showStatusChange' },
            { emit: 'statusFormatChange' },
            { emit: 'validationErrorChange' },
            { emit: 'validationMessageModeChange' },
            { emit: 'valueChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'onBlur' }
        ]);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxProgressBar(element, options);
    }

    writeValue(value: any): void {
        this.value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: (_: any) => void): void { this.change = fn; }
    registerOnTouched(fn: () => void): void { this.touched = fn; }

    _createWidget(element: any) {
        super._createWidget(element);
        this.instance.on('focusOut', (e) => {
            this.eventHelper.fireNgEvent('onBlur', [e]);
        });
    }

    ngOnDestroy() {
        this._destroyWidget();
    }


    ngOnInit() {
        super.ngOnInit();
        if (this.validator) {
            this.validator.createInstanceOnInit = false;
        }
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.validator) {
            this.validator.createInstance(this.element.nativeElement);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-progress-bar',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        CUSTOM_VALUE_ACCESSOR_PROVIDER,
        NestedOptionHost
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'validator': [{ type: ContentChild, args: [DxValidatorComponent, ] },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'isValid': [{ type: Input },],
'max': [{ type: Input },],
'min': [{ type: Input },],
'readOnly': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'showStatus': [{ type: Input },],
'statusFormat': [{ type: Input },],
'validationError': [{ type: Input },],
'validationMessageMode': [{ type: Input },],
'value': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onComplete': [{ type: Output },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onValueChanged': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'isValidChange': [{ type: Output },],
'maxChange': [{ type: Output },],
'minChange': [{ type: Output },],
'readOnlyChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'showStatusChange': [{ type: Output },],
'statusFormatChange': [{ type: Output },],
'validationErrorChange': [{ type: Output },],
'validationMessageModeChange': [{ type: Output },],
'valueChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'onBlur': [{ type: Output },],
'change': [{ type: HostListener, args: ['valueChange', ['$event'], ] },],
'touched': [{ type: HostListener, args: ['onBlur', ['$event'], ] },],
};
}


export class DxProgressBarModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxTemplateModule
  ],
  declarations: [
    DxProgressBarComponent
  ],
  exports: [
    DxProgressBarComponent,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
